import classNames from 'classnames/bind';
import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { CheckCircle, Delete, Upload as UploadIcon, WarningNotifOrange } from '../../assets/icons/Icons';
import Button from '../Button/Button';
import Styles from './FileUpload.module.scss';

const cx = classNames.bind(Styles);

type Props = {
  inboxInfo?: {
    nameFirst: string;
    nameLast: string;
  };
  onUpload: (files: File[]) => void;
  progress: number;
  numFiles: number;
  link: string;
  fileName: string;
  showCopy: boolean;
  showDelete: boolean;
  error: string;
  onDelete: () => void;
  compact?: boolean;
  /** Id for the file input. Pass one when a page renders more than one upload box, or the label
   * fires the first input on the page instead of this one. */
  inputId?: string;
  /** Restricts the file picker, e.g. 'application/pdf'. Drag and drop ignores it, so a caller that
   * cares has to check what it is handed as well. */
  accept?: string;
};

const FileUpload = (props: Props) => {
  const {
    inboxInfo,
    onUpload,
    progress,
    numFiles,
    link,
    fileName,
    showCopy,
    showDelete,
    error,
    onDelete,
    compact,
    inputId = 'upload_file',
    accept,
  } = props;
  const { nameFirst, nameLast } = inboxInfo || { nameFirst: '', nameLast: '' };

  const drop = useRef<HTMLDivElement>(null);
  const drag = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (drop.current) {
      drop.current.addEventListener('dragover', handleDragOver);
      drop.current.addEventListener('drop', handleDrop);
      drop.current.addEventListener('dragenter', handleDragEnter);
      drop.current.addEventListener('dragleave', handleDragLeave);
    }

    return () => {
      if (drop.current) {
        drop.current.removeEventListener('dragover', handleDragOver);
        drop.current.removeEventListener('drop', handleDrop);
        drop.current.removeEventListener('dragenter', handleDragEnter);
        drop.current.removeEventListener('dragleave', handleDragLeave);
      }
    };
  }, []);

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!dragging) {
      setDragging(true);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.files && e.dataTransfer?.files.length) {
      setDragging(false);
      const files = Array.from(e.dataTransfer.files);
      onUpload(files);
    }
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.target !== drag?.current) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.target === drag.current) {
      setDragging(false);
    }
  };

  const handleUpload: ChangeEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!input.current) {
      return;
    }

    const files = Array.from(input.current.files ?? []);

    if (files && files.length) {
      onUpload(files);
    }
  };

  const renderInboxCTA = () => (
    <>
      <p className='show-md'>
        Drag or select files to upload and send to{' '}
        <strong>
          {nameFirst} {nameLast}
        </strong>
      </p>
      <p className='hide-md'>
        Drag and drop or
        <a onClick={() => input.current?.click()}>browse</a>
        to upload and send your files to{' '}
        <strong>
          {nameFirst} {nameLast}
        </strong>
      </p>
    </>
  );

  const renderInternalCTA = () => (
    <>
      <h4 className='show-md'>Drag or Select File{numFiles > 1 ? 's' : ''} for Upload</h4>
      <h4 className='hide-md'>
        Drag and drop or <a onClick={() => input.current?.click()}>browse</a> Files for Upload
      </h4>
    </>
  );

  const renderButtonOrReleaseText = () => {
    return dragging ? (
      <p className={cx('release-text')}>Release to Upload</p>
    ) : (
      <Button className='show-md' onClick={() => input.current?.click()}>
        <label htmlFor={inputId}>Select Files</label>
      </Button>
    );
  };

  const renderHiddenInput = () => (
    <input
      type='file'
      accept={accept}
      name='upload_file'
      id={inputId}
      className={cx('upload-input')}
      ref={input}
      onChange={handleUpload}
      multiple
    />
  );

  if (error) {
    return (
      <div className={cx('fileupload-container', { compact: compact })} ref={drop}>
        <div className={cx('fileupload', { compact: compact })}>
          {UploadIcon}
          <h4>
            {'"' + fileName + '"'} Upload Failed <span>{WarningNotifOrange}</span>
          </h4>
          {renderButtonOrReleaseText()}
          {renderHiddenInput()}
        </div>
        {dragging && <div className={cx('drag-overlay')} ref={drag}></div>}
      </div>
    );
  }

  if (link && fileName) {
    // complete state
    return (
      <div className={cx('fileupload-container', { compact: compact })} ref={drop}>
        <div className={cx('fileupload', 'fileupload--complete', { compact: compact })}>
          {UploadIcon}
          <h4>
            {'"' + fileName + '" '}Upload Complete <span className={cx('check')}>{CheckCircle}</span>
          </h4>
          {inboxInfo ? (
            renderInboxCTA()
          ) : (
            <>
              <div className={cx('file-link') + ' show-md'}>
                <div className={cx('link')}>
                  <a href={link}>{link}</a>
                  {showCopy && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(link);
                        toast.success('File Link copied!');
                      }}>
                      Copy Link
                    </button>
                  )}
                </div>
                {showDelete && (
                  <Button type='ternary' onClick={onDelete}>
                    Delete File {Delete}
                  </Button>
                )}
              </div>
              <h4 className='hide-md'>
                Drag and drop or <a onClick={() => input.current?.click()}>bcompactse</a> Files for Upload
              </h4>
            </>
          )}
          {renderButtonOrReleaseText()}
          {renderHiddenInput()}
        </div>
        {dragging && <div className={cx('drag-overlay')} ref={drag}></div>}
      </div>
    );
  }

  if (progress && progress !== 0) {
    // uploading state
    return (
      <div className={cx('fileupload-container', 'fileupload-container--loading', { compact: compact })}>
        <div className={cx('fileupload', { compact: compact })}>
          {UploadIcon}
          <h4>
            Uploading {numFiles ? numFiles : 1} file{numFiles > 1 ? 's' : ''}
          </h4>
          <progress id='upload-progress' value={progress} max='100' className={cx('upload-progress')} />
        </div>
      </div>
    );
  }

  // default state
  return (
    <div className={cx('fileupload-container', { compact: compact })} ref={drop} data-testid='dropRef'>
      <div className={cx('fileupload', { compact: compact })}>
        {UploadIcon}
        {inboxInfo ? renderInboxCTA() : renderInternalCTA()}
        {renderButtonOrReleaseText()}
        {renderHiddenInput()}
      </div>
      {dragging && <div className={cx('drag-overlay')} ref={drag}></div>}
    </div>
  );
};

export default FileUpload;
